import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Button, Typography, CircularProgress, Checkbox, FormControlLabel,
    LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Tooltip,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { verifyOTP, resendOTP, getUserProfile } from '../../actions/auth';
import { TWO_FACTOR_RESET } from '../../actions/types';
import { API_REGISTRY } from '../../app/config';
import { getOrCreateDeviceId } from '../../app/util';
import { analytics } from '../../hooks/use-analytics';

const OTP_LENGTH = 6;

const OTPVerification = ({ onBack }) => {
    const dispatch = useDispatch();
    const { twoFactor, isLoggedIn } = useSelector(state => state.auth);
    const { tempToken, delivery, maskedDestination, expiresIn } = twoFactor;
    const { UserProfile } = API_REGISTRY;

    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [countdown, setCountdown] = useState(45);
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState('');
    const [attempts, setAttempts] = useState(0);
    const [locked, setLocked] = useState(false);
    const [trustDevice, setTrustDevice] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [verified, setVerified] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setInterval(() => setCountdown(c => c - 1), 1000);
        return () => clearInterval(timer);
    }, [countdown]);

    const submitOtp = async (code) => {
        if (locked || isVerifying) return;
        setIsVerifying(true);
        setError('');

        dispatch(verifyOTP({ otp: code, tempToken, trustDevice, deviceId: await getOrCreateDeviceId('browser') }))
            .then((data) => {
                const { token_string: token, user_id } = data;
                analytics.track('login_2fa_success', { delivery });
                setIsVerifying(false);
                setVerified(true);
                setTimeout(() => {
                    dispatch(getUserProfile({
                        api: UserProfile.uri,
                        request: { ...UserProfile.payload, token_string: token, user_id },
                    })).catch(() => setVerified(false));
                }, 800);
            })
            .catch((msg) => {
                const nextAttempts = attempts + 1;
                setAttempts(nextAttempts);
                setIsVerifying(false);
                setOtp(Array(OTP_LENGTH).fill(''));
                inputRefs.current[0]?.focus();
                if (nextAttempts >= 3) {
                    setLocked(true);
                    setError('Too many failed attempts. Please sign in again.');
                } else {
                    setError(msg || 'Invalid code. Please try again.');
                }
            });
    };

    const handleChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const next = [...otp];
        next[index] = value.slice(-1);
        setOtp(next);
        setError('');
        if (value && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            const code = otp.join('');
            if (code.length === OTP_LENGTH) submitOtp(code);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (pasted.length > 0) {
            setOtp([...pasted.split(''), ...Array(OTP_LENGTH).fill('')].slice(0, OTP_LENGTH));
            inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
        }
    };

    const handleResend = () => {
        dispatch(resendOTP({ tempToken }));
        setCountdown(45);
        setOtp(Array(OTP_LENGTH).fill(''));
        setError('');
        setAttempts(0);
        inputRefs.current[0]?.focus();
    };

    const handleBack = () => {
        dispatch({ type: TWO_FACTOR_RESET });
        if (onBack) onBack();
    };

    const otpCode = otp.join('');
    const deliveryLabel = delivery === 'sms' ? 'your phone' : 'your email';

    return (
        <Box>
            {isLoggedIn && (
                <LinearProgress
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#00415F' },
                        backgroundColor: '#b3d9e8',
                    }}
                />
            )}
            {verified ? (
                <Box sx={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #efefef',
                    borderRadius: '12px',
                    p: 2.5, mt: 0.5,
                    py: 5, gap: 1.5,
                }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        backgroundColor: '#e8f4f9', border: '2px solid #b3d9e8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '@keyframes popIn': {
                            '0%':   { transform: 'scale(0.5)', opacity: 0 },
                            '70%':  { transform: 'scale(1.15)' },
                            '100%': { transform: 'scale(1)',   opacity: 1 },
                        },
                        animation: 'popIn 0.35s ease-out forwards',
                    }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#00415F' }} />
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#00415F' }}>
                        Verified!
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#888' }}>
                        Signing you in…
                    </Typography>
                </Box>
            ) : (
            <Box sx={{
                backgroundColor: '#f9fafb',
                border: '1px solid #efefef',
                borderRadius: '12px',
                p: 2.5,
                mt: 0.5,
            }}>

            {/* Icon + subtitle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: '#e8f4f9', border: '1.5px solid #b3d9e8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <LockOutlinedIcon sx={{ fontSize: 20, color: '#00415F' }} />
                </Box>
                <Typography sx={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                    We sent a 6‑digit code to <strong>{deliveryLabel}</strong>
                    {maskedDestination && (
                        <Box component="span" sx={{ color: '#333', ml: 0.5 }}>({maskedDestination})</Box>
                    )}
                </Typography>
            </Box>

            {/* OTP input boxes */}
            <Box
                sx={{ display: 'flex', gap: '10px', mb: 2, justifyContent: 'flex-start', alignItems: 'center' }}
                onPaste={handlePaste}
            >
                {otp.map((digit, i) => (
                    <Box key={i} sx={{ position: 'relative', width: 44, height: 52, flexShrink: 0 }}>
                        <input
                            ref={el => inputRefs.current[i] = el}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            disabled={locked || isVerifying}
                            onChange={e => handleChange(i, e.target.value)}
                            onKeyDown={e => handleKeyDown(i, e)}
                            style={{
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                padding: 0,
                                textAlign: 'center',
                                lineHeight: '52px',
                                fontSize: 22,
                                fontWeight: 600,
                                fontFamily: 'inherit',
                                border: `2px solid ${error ? '#d32f2f' : digit ? '#00415F' : '#d0d5dd'}`,
                                borderRadius: 8,
                                outline: 'none',
                                backgroundColor: locked ? '#f5f5f5' : '#f8f8f8',
                                color: '#333',
                                transition: 'border-color 0.15s',
                                cursor: locked ? 'not-allowed' : 'text',
                                caretColor: '#00415F',
                            }}
                            onFocus={e => { e.target.style.borderColor = error ? '#d32f2f' : '#00415F'; }}
                            onBlur={e => { e.target.style.borderColor = error ? '#d32f2f' : digit ? '#00415F' : '#d0d5dd'; }}
                        />
                    </Box>
                ))}
            </Box>

            {/* Error message */}
            {error && (
                <Typography sx={{ fontSize: 12, color: '#d32f2f', textAlign: 'center', mb: 1.5 }}>
                    {error}
                </Typography>
            )}

            {/* Trust device option */}
            <Box
                onClick={() => { if (!locked && !isVerifying) setTrustDevice(v => !v); }}
                sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                    backgroundColor: trustDevice ? '#e8f4f9' : '#fafafa',
                    border: `1px solid ${trustDevice ? '#b3d9e8' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    px: 1.5, py: 1,
                    mb: 1.5,
                    cursor: locked || isVerifying ? 'default' : 'pointer',
                    transition: 'background-color 0.15s, border-color 0.15s',
                }}
            >
                <Checkbox
                    checked={trustDevice}
                    onChange={e => setTrustDevice(e.target.checked)}
                    disabled={locked || isVerifying}
                    size="small"
                    sx={{ color: '#00415F', '&.Mui-checked': { color: '#00415F' }, p: 0, mt: 0.2 }}
                />
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#00415F' }}>
                            Trust this browser
                        </Typography>
                        <Tooltip
                            title="Trusting this browser skips Two-Step Authentication for 7 days. Only enable this on a personal or secure browser that only you have access to."
                            placement="top"
                            arrow
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#9ca3af', cursor: 'help' }} onClick={e => e.stopPropagation()} />
                        </Tooltip>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4, mt: 0.25 }}>
                        Skip Two-Step Authentication on your next sign-in from this browser
                    </Typography>
                </Box>
            </Box>

            {/* Verify button */}
            {!locked && (
                <Button
                    className="signInBtn"
                    type="button"
                    fullWidth
                    variant="contained"
                    disabled={otpCode.length < OTP_LENGTH || isVerifying}
                    onClick={() => submitOtp(otpCode)}
                    sx={{ mb: 2 }}
                >
                    {isVerifying
                        ? <CircularProgress size={20} color="inherit" />
                        : 'Verify'}
                </Button>
            )}

            {/* Resend + Back row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 38 }}>
                {countdown > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34 }}>
                            <CircularProgress
                                variant="determinate"
                                value={100}
                                size={34}
                                thickness={3}
                                sx={{ color: '#e8e8e8', position: 'absolute' }}
                            />
                            <CircularProgress
                                variant="determinate"
                                value={(countdown / 45) * 100}
                                size={34}
                                thickness={3}
                                sx={{ color: '#00415F', position: 'absolute' }}
                            />
                            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#666', lineHeight: 1 }}>
                                {countdown}s
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                            Resend Code
                        </Typography>
                    </Box>
                ) : (
                    <Button
                        type="button"
                        size="small"
                        variant="text"
                        onClick={handleResend}
                        sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            padding: 0,
                            color: '#00415F',
                            '&:hover': { backgroundColor: 'transparent', color: '#002333' },
                            minWidth: 0,
                        }}
                    >
                        Resend Code
                    </Button>
                )}

                <Button
                    type="button"
                    size="small"
                    variant="text"
                    onClick={handleBack}
                    sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 400,
                        padding: 0,
                        color: '#555',
                        '&:hover': { backgroundColor: 'transparent', color: '#002333' },
                        minWidth: 0,
                    }}
                >
                    ← Back to Sign In
                </Button>
            </Box>

            {/* Need help link */}
            {/* <Box sx={{ textAlign: 'left', mt: 1 }}>
                <Button
                    type="button"
                    size="small"
                    variant="text"
                    onClick={() => setShowHelp(true)}
                    sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        color: '#9ca3af',
                        padding: 0,
                        '&:hover': { backgroundColor: 'transparent', color: '#6b7280' },
                        minWidth: 0,
                    }}
                >
                    Can't access your code? Need help?
                </Button>
            </Box> */}

            </Box>
            )}

            {/* Help Dialog */}
            <Dialog
                open={showHelp}
                onClose={() => setShowHelp(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        padding: '8px',
                        maxWidth: 420,
                        width: '100%',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: '#111', pb: 0 }}>
                    Can't access your verification code?
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: '#555', mt: 1, mb: 2.5, lineHeight: 1.6 }}>
                        If you've lost access to your {delivery === 'sms' ? 'phone' : 'email'}, your account
                        administrator or LodgIQ support can reset your Two-Step Authentication and restore access.
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    {/* Contact LodgIQ support */}
                    <Box sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#333', mb: 0.5 }}>
                            Contact LodgIQ support
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                            Email us and we'll verify your identity and restore access within
                            one business day.
                        </Typography>
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            href="mailto:support@lodgiq.com?subject=Two-Step Authentication Access Recovery"
                            component="a"
                            sx={{
                                mt: 1,
                                textTransform: 'none',
                                fontSize: 12,
                                borderColor: '#00415F',
                                color: '#00415F',
                                borderRadius: '8px',
                                '&:hover': { borderColor: '#002333', color: '#002333', backgroundColor: '#e8f4f9' },
                            }}
                        >
                            Email support@lodgiq.com
                        </Button>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        type="button"
                        onClick={() => setShowHelp(false)}
                        variant="contained"
                        size="small"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            backgroundColor: '#00415F',
                            '&:hover': { backgroundColor: '#002333' },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OTPVerification;
